import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  CreateFoodIntegrationPayload,
  FoodIntegration,
  FoodProviderCode,
  FoodProviderInfo,
  FOOD_PROVIDER_LABELS,
  ProviderField,
  PROVIDER_FIELD_GROUP_LABELS,
  RemoteStore,
  UpdateFoodIntegrationPayload
} from '@/types/FoodDelivery.interface';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import ProviderFieldInput from './ProviderFieldInput';

interface Props {
  open: boolean;
  branchId: number;
  /** Düzenleme modunda mevcut entegrasyon; oluşturma modunda null. */
  editing: FoodIntegration | null;
  onClose: () => void;
  onSaved: () => void;
}

type FieldValues = Record<string, unknown>;

const STEPS = [
  'Platform',
  'Bağlantı Bilgileri',
  'Mağaza',
  'Ayarlar',
  'Doğrulama'
];

/**
 * Kurulum sihirbazı (plan Faz 1.5).
 *
 * Alanlar backend'deki adapter şemasından (`GET /food-delivery/providers`)
 * üretilir; burada provider'a özel `if` YOKTUR. Eskiden form sabit 3 credential
 * alanı gösteriyordu ve Trendyol GO'nun zorunlu `supplierId` alanı hiç
 * bulunmadığı için panelden entegrasyon kurmak imkânsızdı.
 */
const IntegrationFormDialog: React.FC<Props> = ({
  open,
  branchId,
  editing,
  onClose,
  onSaved
}) => {
  const isEdit = !!editing;

  const [providers, setProviders] = useState<FoodProviderInfo[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [providerCode, setProviderCode] = useState<FoodProviderCode | ''>('');
  const [displayName, setDisplayName] = useState('');
  const [values, setValues] = useState<FieldValues>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sihirbazda oluşturulan/düzenlenen entegrasyonun kimliği (mağaza seçimi ve
  // doğrulama bu id üzerinden yapılır — credential'lar şifreli saklandığı için
  // ikinci bir kanaldan düz metin taşınmaz).
  const [integrationId, setIntegrationId] = useState<number | null>(null);
  const [stores, setStores] = useState<RemoteStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [validateResult, setValidateResult] = useState<{
    ok: boolean;
    message?: string;
  } | null>(null);
  const [validating, setValidating] = useState(false);

  const provider = useMemo(
    () => providers.find((p) => p.code === providerCode) ?? null,
    [providers, providerCode]
  );
  const schema = provider?.configSchema;
  const credentialFields = useMemo(
    () => (schema?.fields ?? []).filter((f) => f.group === 'CREDENTIAL'),
    [schema]
  );
  const configFields = useMemo(
    () => (schema?.fields ?? []).filter((f) => f.group === 'CONFIG'),
    [schema]
  );
  const savedKeys = editing?.credentialKeys ?? [];

  useEffect(() => {
    if (!open) return;
    setProvidersLoading(true);
    foodDeliveryService
      .listProviders()
      .then(setProviders)
      .catch(() => setError('Platform listesi alınamadı.'))
      .finally(() => setProvidersLoading(false));
  }, [open]);

  // Dialog her açıldığında durumu sıfırla / düzenleme verisini yükle.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setValidateResult(null);
    setStores([]);
    setStoresError(null);
    if (editing) {
      setProviderCode(editing.providerCode);
      setDisplayName(editing.displayName ?? '');
      setIntegrationId(editing.id);
      setValues({ ...(editing.config ?? {}) });
      setStep(1);
    } else {
      setProviderCode('');
      setDisplayName('');
      setIntegrationId(null);
      setValues({});
      setStep(0);
    }
  }, [open, editing]);

  // Platform seçilince CONFIG alanlarının varsayılanlarını doldur.
  useEffect(() => {
    if (!schema || isEdit) return;
    const defaults: FieldValues = {};
    for (const f of schema.fields) {
      if (f.default !== undefined) defaults[f.key] = f.default;
    }
    setValues((prev) => ({ ...defaults, ...prev }));
  }, [schema, isEdit]);

  const setValue = useCallback((key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Doldurulmuş credential/config değerlerini ayrıştırır (boşlar gönderilmez). */
  const splitPayload = () => {
    const credentials: Record<string, unknown> = {};
    const config: Record<string, unknown> = {};
    for (const f of schema?.fields ?? []) {
      const v = values[f.key];
      if (v === undefined || v === '') continue;
      if (f.group === 'CREDENTIAL') credentials[f.key] = v;
      else config[f.key] = v;
    }
    return {
      credentials: Object.keys(credentials).length ? credentials : undefined,
      config: Object.keys(config).length ? config : undefined
    };
  };

  const missingRequired: ProviderField[] = credentialFields.filter(
    (f) =>
      f.required &&
      !savedKeys.includes(f.key) &&
      (values[f.key] === undefined || values[f.key] === '')
  );

  /** Bağlantı bilgileri adımından sonra kaydeder (mağaza seçimi id ister). */
  const persist = async (): Promise<number | null> => {
    setSaving(true);
    setError(null);
    try {
      const { credentials, config } = splitPayload();
      if (isEdit && editing) {
        const payload: UpdateFoodIntegrationPayload = {
          displayName: displayName || undefined,
          credentials,
          config
        };
        await foodDeliveryService.update(editing.id, payload);
        onSaved();
        return editing.id;
      }
      if (integrationId) {
        // Sihirbaz içinde geri dönüp tekrar ilerlerse aynı kaydı günceller.
        await foodDeliveryService.update(integrationId, {
          displayName: displayName || undefined,
          credentials,
          config
        });
        onSaved();
        return integrationId;
      }
      const payload: CreateFoodIntegrationPayload = {
        branchId,
        providerCode: providerCode as FoodProviderCode,
        displayName: displayName || undefined,
        credentials,
        config
      };
      const created = await foodDeliveryService.create(payload);
      setIntegrationId(created.id);
      onSaved();
      return created.id;
    } catch (e) {
      setError(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Entegrasyon kaydedilemedi.'
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const loadStores = async (id: number) => {
    setStoresLoading(true);
    setStoresError(null);
    try {
      setStores(await foodDeliveryService.listRemoteStores(id));
    } catch (e) {
      setStoresError(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Mağaza listesi alınamadı.'
      );
    } finally {
      setStoresLoading(false);
    }
  };

  const pickStore = async (store: RemoteStore) => {
    if (!integrationId) return;
    setSaving(true);
    try {
      await foodDeliveryService.update(integrationId, {
        externalRestaurantId: store.id,
        credentials: { storeId: store.id }
      });
      setValue('storeId', store.id);
      onSaved();
      setStep(3);
    } catch (e) {
      setStoresError(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Mağaza kaydedilemedi.'
      );
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async (id: number) => {
    setValidating(true);
    try {
      setValidateResult(await foodDeliveryService.validate(id));
      onSaved();
    } catch (e) {
      setValidateResult({
        ok: false,
        message:
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Doğrulama başarısız.'
      });
    } finally {
      setValidating(false);
    }
  };

  const handleNext = async () => {
    if (step === 0) {
      setStep(1);
      return;
    }
    if (step === 1) {
      const id = await persist();
      if (!id) return;
      setStep(2);
      if (provider?.supportsStoreDiscovery) void loadStores(id);
      return;
    }
    if (step === 2) {
      setStep(3);
      return;
    }
    if (step === 3) {
      const id = await persist();
      if (!id) return;
      setStep(4);
      void runValidate(id);
      return;
    }
    onClose();
  };

  const nextDisabled =
    saving ||
    (step === 0 && !providerCode) ||
    (step === 1 && missingRequired.length > 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? 'Entegrasyonu Düzenle' : 'Yeni Yemek Platformu Entegrasyonu'}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {providersLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {/* 1) Platform seçimi */}
            {step === 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Bu şubeyi hangi yemek platformuna bağlamak istiyorsunuz?
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Platform"
                  value={providerCode}
                  onChange={(e) =>
                    setProviderCode(e.target.value as FoodProviderCode)
                  }
                >
                  {providers.map((p) => (
                    <MenuItem key={p.code} value={p.code}>
                      {FOOD_PROVIDER_LABELS[p.code]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  label="Görünen Ad"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="örn. Merkez Şube - Trendyol GO"
                  helperText="Listede bu adla görünür (isteğe bağlı)."
                />
              </>
            )}

            {/* 2) Bağlantı bilgileri — şemadan üretilir */}
            {step === 1 && schema && (
              <>
                {(schema.notes ?? []).map((note) => (
                  <Alert key={note} severity="info">
                    {note}
                  </Alert>
                ))}
                {schema.docsUrl && (
                  <Typography variant="caption">
                    Resmi doküman:{' '}
                    <Link
                      href={schema.docsUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {schema.docsUrl}
                    </Link>
                  </Typography>
                )}
                <Divider>
                  <Chip
                    size="small"
                    label={PROVIDER_FIELD_GROUP_LABELS.CREDENTIAL}
                  />
                </Divider>
                {credentialFields.map((f) => (
                  <ProviderFieldInput
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={setValue}
                    saved={savedKeys.includes(f.key)}
                  />
                ))}
                {credentialFields.length === 0 && (
                  <Alert severity="warning">
                    Bu platform için kurulum alanı tanımlı değil.
                  </Alert>
                )}
              </>
            )}

            {/* 3) Mağaza seçimi */}
            {step === 2 && (
              <>
                {!provider?.supportsStoreDiscovery ? (
                  <Alert severity="info">
                    Bu platform mağaza listelemeyi desteklemiyor. Mağaza
                    kimliğini bir önceki adımda elle girebilirsiniz.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Bu şubeye bağlanacak platform mağazasını seçin. Menü,
                      fiyat ve uygunluk işlemleri bu mağaza üzerinden yapılır.
                    </Typography>
                    {storesError && (
                      <Alert severity="error">{storesError}</Alert>
                    )}
                    {storesLoading ? (
                      <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        {stores.map((s) => (
                          <Button
                            key={s.id}
                            variant={
                              values.storeId === s.id ? 'contained' : 'outlined'
                            }
                            startIcon={<StorefrontIcon />}
                            disabled={saving}
                            onClick={() => pickStore(s)}
                            sx={{ justifyContent: 'flex-start' }}
                          >
                            <Stack alignItems="flex-start">
                              <span>
                                {s.name} (ID: {s.id})
                              </span>
                              {s.address && (
                                <Typography variant="caption">
                                  {s.address}
                                </Typography>
                              )}
                            </Stack>
                          </Button>
                        ))}
                        {stores.length === 0 && !storesError && (
                          <Alert severity="info">
                            Mağaza bulunamadı. Platform panelinizden mağaza
                            tanımlı olduğundan emin olun.
                          </Alert>
                        )}
                      </Stack>
                    )}
                    {/* Mağazasız entegrasyon SİPARİŞ DE ALMAZ: mağaza
                        belirtilmeyen sorgu, satıcının tüm şubelerinin
                        siparişlerini bu şubeye taşıdığı için engellenir. */}
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Mağaza seçmeden devam edebilirsiniz, ancak entegrasyon
                      <strong> sipariş almaya başlamaz</strong> ve “Kurulum
                      Bekliyor” durumunda kalır. Menü, fiyat ve uygunluk
                      özellikleri de mağaza seçilene kadar çalışmaz.
                    </Alert>
                  </>
                )}
              </>
            )}

            {/* 4) Davranış ayarları */}
            {step === 3 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Bu ayarlar sonradan da değiştirilebilir.
                </Typography>
                {configFields.map((f) => (
                  <ProviderFieldInput
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={setValue}
                  />
                ))}
                {configFields.length === 0 && (
                  <Alert severity="info">
                    Bu platform için ayarlanabilir davranış bulunmuyor.
                  </Alert>
                )}
              </>
            )}

            {/* 5) Doğrulama */}
            {step === 4 && (
              <>
                {validating ? (
                  <Stack alignItems="center" spacing={2} py={3}>
                    <CircularProgress />
                    <Typography variant="body2">
                      Platforma bağlanılıyor…
                    </Typography>
                  </Stack>
                ) : validateResult?.ok ? (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    <AlertTitle>Bağlantı başarılı</AlertTitle>
                    Entegrasyon aktif. Sıradaki adım: <b>Menü Eşleştirme</b>
                    ekranından platform ürünlerini kendi ürünlerinizle
                    eşleştirin.
                  </Alert>
                ) : (
                  <Alert severity="error" icon={<ErrorOutlineIcon />}>
                    <AlertTitle>Bağlantı kurulamadı</AlertTitle>
                    {validateResult?.message ?? 'Bilinmeyen bir hata oluştu.'}
                  </Alert>
                )}
                {!validating && !validateResult?.ok && integrationId && (
                  <Button
                    variant="outlined"
                    onClick={() => void runValidate(integrationId)}
                  >
                    Tekrar Dene
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {step === 4 ? 'Kapat' : 'İptal'}
        </Button>
        {step > 0 && step < 4 && (
          <Button onClick={() => setStep(step - 1)} disabled={saving}>
            Geri
          </Button>
        )}
        {step < 4 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={nextDisabled}
          >
            {saving
              ? 'Kaydediliyor…'
              : step === 3
                ? 'Kaydet ve Doğrula'
                : step === 2
                  ? 'Atla / Devam'
                  : 'Devam'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IntegrationFormDialog;
